import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { PlusCircle, Trash2, MoveUp, MoveDown, Plus, Eye, Save, Upload } from 'lucide-react';
import { ProjectService } from '@/services/ProjectService';
import { useToast } from '@/hooks/use-toast';
import PreviewModal from '@/components/AdditionalInfo/PreviewModal';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AVAILABLE_ROOM_TYPES } from '@/utils/flow-data';
import { AssetsConfig } from '@/services/AssetsConfig';

const QuestionBuilder = () => {
    const [currentCategory, setCurrentCategory] = useState(null);
    const [showJson, setShowJson] = useState(false);
    const [currentRoom, setCurrentRoom] = useState('kitchen');
    const [formData, setFormData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showPreview, setShowPreview] = useState(false);
    const { toast } = useToast();
    const [isPublishing, setIsPublishing] = useState(false);

    // const AVAILABLE_ROOM_TYPES = [
    //     { value: 'kitchen', label: 'Kitchen' },
    //     { value: 'bathroom', label: 'Bathroom' },
    //     { value: 'laundry', label: 'Laundry' },
    //     { value: 'mudroom', label: 'Mudroom' },
    //     { value: 'default', label: 'Default' },
    //     { value: 'other', label: 'Other' },
    //     { value: 'living_room', label: 'Living Room' },
    //     { value: 'dining_room', label: 'Dining Room' },
    //     { value: 'office', label: 'Office' }
    //     ];
    
    const questionTypes = [
        { value: 'text', label: 'Text Input' },
        { value: 'dropdown', label: 'Dropdown' },
        { value: 'multiple_select', label: 'Multiple Select' },
        { value: 'radio', label: 'Radio Buttons' },
        { value: 'picture_select', label: 'Picture Selection' },
        { value: 'file_upload', label: 'File Upload' }
    ];


    const loadConfig = async () => {
        try {
            setIsLoading(true);
            const configData = await AssetsConfig.getConfigJSON({ fileName: 'AdditionalQuestions' });
            if (configData) {
                setFormData(configData);
                if (configData[currentRoom]?.categories?.length > 0) {
                    setCurrentCategory(configData[currentRoom].categories[0].id);
                }
            }
        } catch (error) {
            console.error("Error loading configuration:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadConfig();
    }, []);

    const handlePublish = async () => {
        try {
            setIsPublishing(true);
            await ProjectService.uploadConfigJSON(formData, "AdditionalQuestions.json");
            toast({
                title: "Published Successfully",
                description: "Your configuration has been published.",
            });
        } catch (error) {
            console.error('Error publishing:', error);
            toast({
                title: "Error Publishing",
                description: "Failed to publish configuration.",
                variant: "destructive",
            });
        } finally {
            setIsPublishing(false);
        }
    };

    const addCategory = () => {
        const newCategory = {
          id: `category_${Date.now()}`,
          name: 'New Section',
          icon: '📁',
          questions: []
        };
        
        setFormData({
          ...formData,
          [currentRoom]: {
            ...formData[currentRoom],
            categories: [...formData[currentRoom].categories, newCategory]
          }
        });
        setCurrentCategory(newCategory.id);
      };
      
      const updateCategory = (categoryId, field, value) => {
        setFormData({
          ...formData,
          [currentRoom]: {
            ...formData[currentRoom],
            categories: formData[currentRoom].categories.map(c =>
              c.id === categoryId ? { ...c, [field]: value } : c
            )
          }
        });
      };
      

    const removeCategory = (categoryId) => {
        setFormData({
          ...formData,
          [currentRoom]: {
            ...formData[currentRoom],
            categories: formData[currentRoom].categories.filter(c => c.id !== categoryId)
          }
        });
        if (currentCategory === categoryId) {
          setCurrentCategory(null);
        }
      };
      
      const addQuestion = (categoryId) => {
        const newQuestion = {
          id: `question_${Date.now()}`,
          type: 'text',
          question: 'New Question',
          icon: '❓',
          required: false,
          visible: true,
          options: [],
          helpText: '',
          fileConfig: {
            allowedTypes: [],
            maxSize: 5,
            multiple: false
          }
        };
      
        setFormData({
          ...formData,
          [currentRoom]: {
            ...formData[currentRoom],
            categories: formData[currentRoom].categories.map(c =>
              c.id === categoryId 
                ? { ...c, questions: [...c.questions, newQuestion] }
                : c
            )
          }
        });
      };
      
      const updateQuestion = (categoryId, questionId, field, value) => {
        setFormData({
          ...formData,
          [currentRoom]: {
            ...formData[currentRoom],
            categories: formData[currentRoom].categories.map(c => {
              if (c.id === categoryId) {
                const updatedQuestions = c.questions.map(q =>
                  q.id === questionId ? { ...q, [field]: value } : q
                );
                return { ...c, questions: updatedQuestions };
              }
              return c;
            })
          }
        });
      };
      
      const removeQuestion = (categoryId, questionId) => {
        setFormData({
          ...formData,
          [currentRoom]: {
            ...formData[currentRoom],
            categories: formData[currentRoom].categories.map(c => {
              if (c.id === categoryId) {
                return {
                  ...c,
                  questions: c.questions.filter(q => q.id !== questionId)
                };
              }
              return c;
            })
          }
        });
      };
      
      const moveQuestion = (categoryId, questionId, direction) => {
        setFormData({
          ...formData,
          [currentRoom]: {
            ...formData[currentRoom],
            categories: formData[currentRoom].categories.map(c => {
              if (c.id === categoryId) {
                const questions = [...c.questions];
                const index = questions.findIndex(q => q.id === questionId);
                if (index === -1) return c;
                
                const newIndex = direction === 'up' ? index - 1 : index + 1;
                if (newIndex < 0 || newIndex >= questions.length) return c;
                
                [questions[index], questions[newIndex]] = [questions[newIndex], questions[index]];
                return { ...c, questions: questions };
              }
              return c;
            })
          }
        });
      };
      
      const addOption = (categoryId, questionId) => {
        setFormData({
          ...formData,
          [currentRoom]: {
            ...formData[currentRoom],
            categories: formData[currentRoom].categories.map(c => {
              if (c.id === categoryId) {
                const updatedQuestions = c.questions.map(q => {
                  if (q.id === questionId) {
                    const newOption = q.type === 'picture_select'
                      ? {
                          value: `option_${q.options.length + 1}`,
                          label: `Option ${q.options.length + 1}`,
                          imageWidth: 300,
                          imageHeight: 200
                        }
                      : `Option ${q.options.length + 1}`;
                    return {
                      ...q,
                      options: [...(q.options || []), newOption]
                    };
                  }
                  return q;
                });
                return { ...c, questions: updatedQuestions };
              }
              return c;
            })
          }
        });
      };
      
      const updateOption = (categoryId, questionId, optionIndex, field, value) => {
        setFormData({
          ...formData,
          [currentRoom]: {
            ...formData[currentRoom],
            categories: formData[currentRoom].categories.map(c => {
              if (c.id === categoryId) {
                const updatedQuestions = c.questions.map(q => {
                  if (q.id === questionId) {
                    const updatedOptions = q.options.map((opt, idx) => {
                      if (idx === optionIndex) {
                        return q.type === 'picture_select'
                          ? { ...opt, [field]: value }
                          : value;
                      }
                      return opt;
                    });
                    return { ...q, options: updatedOptions };
                  }
                  return q;
                });
                return { ...c, questions: updatedQuestions };
              }
              return c;
            })
          }
        });
      };
      
      const getAvailableQuestions = (categoryId, currentQuestionId) => {
        const category = formData[currentRoom].categories.find(c => c.id === categoryId);
        if (!category) return [];
        
        const currentIndex = category.questions.findIndex(q => q.id === currentQuestionId);
        return category.questions
          .slice(0, currentIndex)
          .map(q => ({
            value: q.id,
            label: q.question,
            type: q.type,
            options: q.options
          }));
      };

    
      const renderQuestionOptions = (category, question) => {
        if (!['dropdown', 'multiple_select', 'radio', 'picture_select'].includes(question.type)) {
            return null;
        }
    
        const handleImageUpload = async (optionIndex, file) => {
            try {
                const result = await ProjectService.uploadAssetImage(file);
                const updatedOptions = [...question.options];
                updatedOptions[optionIndex] = {
                    ...updatedOptions[optionIndex],
                    value: `option_${optionIndex + 1}`, // Automatically set value
                    imageUrl: result.url
                };
                updateQuestion(category.id, question.id, 'options', updatedOptions);
            } catch (error) {
                console.error('Failed to upload image:', error);
            }
        };
    
        return (
            <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between">
                    <Label>Options</Label>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => addOption(category.id, question.id)}
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Option
                    </Button>
                </div>
                <div className="space-y-2">
                    {question.options.map((option, index) => (
                        <div key={index} className="flex gap-2">
                            {question.type === 'picture_select' ? (
                                <div className="flex gap-2 w-full items-center">
                                    <Input
                                        value={option.label}
                                        onChange={(e) => updateOption(category.id, question.id, index, 'label', e.target.value)}
                                        placeholder="Option label"
                                        className="flex-1"
                                    />
                                    <div className="flex gap-2 items-center">
                                        <input
                                            type="file"
                                            id={`image-upload-${index}`}
                                            className="hidden"
                                            accept="image/*"
                                            onChange={(e) => handleImageUpload(index, e.target.files[0])}
                                        />
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => document.getElementById(`image-upload-${index}`).click()}
                                        >
                                            <Upload className="w-4 h-4 mr-1" />
                                            Upload Image
                                        </Button>
                                        {option.imageUrl && (
                                            <div className="h-16 w-24 border rounded overflow-hidden">
                                                <img 
                                                    src={option.imageUrl} 
                                                    alt={option.label} 
                                                    className="h-full w-full object-contain"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <Input
                                    value={option}
                                    onChange={(e) => updateOption(category.id, question.id, index, null, e.target.value)}
                                    placeholder="Option text"
                                    className="flex-1"
                                />
                            )}
                            <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => {
                                    const updatedOptions = question.options.filter((_, i) => i !== index);
                                    updateQuestion(category.id, question.id, 'options', updatedOptions);
                                }}
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    ))}
                </div>
            </div>
        );
    };


    const renderConditionBuilder = (category, question) => {
        if (!question.conditions) {
            return (
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateQuestion(category.id, question.id, 'conditions', [{
                        questionId: '',
                        operator: 'equals',
                        value: ''
                    }])}
                >
                    Add Condition
                </Button>
            );
        }

        return (
            <div className="space-y-2">
                {question.conditions.map((condition, index) => {
                    const dependentQuestion = category.questions.find(q => q.id === condition.questionId);

                    return (
                        <div key={index} className="flex gap-2 items-end">
                            <div className="flex-1">
                                <Label>Depends on Question</Label>
                                <Select
                                    value={condition.questionId}
                                    onValueChange={(value) => {
                                        const updatedConditions = [...question.conditions];
                                        updatedConditions[index] = { ...condition, questionId: value, value: '' };
                                        updateQuestion(category.id, question.id, 'conditions', updatedConditions);
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select question" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {getAvailableQuestions(category.id, question.id).map(q => (
                                            <SelectItem key={q.value} value={q.value}>
                                                {q.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="w-32">
                                <Label>Operator</Label>
                                <Select
                                    value={condition.operator}
                                    onValueChange={(value) => {
                                        const updatedConditions = [...question.conditions];
                                        updatedConditions[index] = { ...condition, operator: value };
                                        updateQuestion(category.id, question.id, 'conditions', updatedConditions);
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="equals">Equals</SelectItem>
                                        <SelectItem value="not_equals">Not Equals</SelectItem>
                                        <SelectItem value="contains">Contains</SelectItem>
                                        <SelectItem value="not_contains">Doesn't Contain</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex-1">
                                <Label>Value</Label>
                                {dependentQuestion?.type === 'dropdown' || dependentQuestion?.type === 'radio' || dependentQuestion?.type === 'picture_select' ? (
                                    <Select
                                        value={condition.value}
                                        onValueChange={(value) => {
                                            const updatedConditions = [...question.conditions];
                                            updatedConditions[index] = { ...condition, value };
                                            updateQuestion(category.id, question.id, 'conditions', updatedConditions);
                                        }}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select value" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {dependentQuestion.options.map((opt) => (
                                                <SelectItem
                                                    key={opt.value || opt}
                                                    value={opt.value || opt}
                                                >
                                                    {opt.label || opt}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    <Input
                                        value={condition.value}
                                        onChange={(e) => {
                                            const updatedConditions = [...question.conditions];
                                            updatedConditions[index] = { ...condition, value: e.target.value };
                                            updateQuestion(category.id, question.id, 'conditions', updatedConditions);
                                        }}
                                        placeholder="Enter value"
                                    />
                                )}
                            </div>

                            <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => {
                                    const updatedConditions = question.conditions.filter((_, i) => i !== index);
                                    updateQuestion(
                                        category.id,
                                        question.id,
                                        'conditions',
                                        updatedConditions.length ? updatedConditions : undefined
                                    );
                                }}
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    );
                })}

                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                        const updatedConditions = [...(question.conditions || []), {
                            questionId: '',
                            operator: 'equals',
                            value: ''
                        }];
                        updateQuestion(category.id, question.id, 'conditions', updatedConditions);
                    }}
                >
                    Add Another Condition
                </Button>
            </div>
        );
    };

    const renderQuestion = (category, question) => (
        <Card key={question.id} className="mb-4">
            <CardContent className="pt-6">
                <div className="flex gap-2 mb-4">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => moveQuestion(category.id, question.id, 'up')}
                    >
                        <MoveUp className="w-4 h-4" />
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => moveQuestion(category.id, question.id, 'down')}
                    >
                        <MoveDown className="w-4 h-4" />
                    </Button>
                    <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => removeQuestion(category.id, question.id)}
                    >
                        <Trash2 className="w-4 h-4" />
                    </Button>
                </div>

                <div className="space-y-4">
                    <div className="flex gap-2">
                        <div className="flex-1">
                            <Label>Question Text</Label>
                            <Input
                                value={question.question}
                                onChange={(e) => updateQuestion(category.id, question.id, 'question', e.target.value)}
                                placeholder="Enter question text"
                            />
                        </div>
                        <div className="w-24">
                            <Label>Icon</Label>
                            <Input
                                value={question.icon}
                                onChange={(e) => updateQuestion(category.id, question.id, 'icon', e.target.value)}
                                placeholder="Icon"
                            />
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="flex-1">
                            <Label>Question Type</Label>
                            <Select
                                value={question.type}
                                onValueChange={(value) => updateQuestion(category.id, question.id, 'type', value)}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {questionTypes.map(type => (
                                        <SelectItem key={type.value} value={type.value}>
                                            {type.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Required</Label>
                            <div>
                                <Switch
                                    checked={question.required}
                                    onCheckedChange={(checked) => updateQuestion(category.id, question.id, 'required', checked)}
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Visible</Label>
                            <div>
                                <Switch
                                    checked={question.visible}
                                    onCheckedChange={(checked) => updateQuestion(category.id, question.id, 'visible', checked)}
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <Label>Help Text</Label>
                        <Input
                            value={question.helpText}
                            onChange={(e) => updateQuestion(category.id, question.id, 'helpText', e.target.value)}
                            placeholder="Enter help text"
                        />
                    </div>

                    <div>
                        <Label>Conditions</Label>
                        {renderConditionBuilder(category, question)}
                    </div>

                    {renderQuestionOptions(category, question)}

                    {question.type === 'file_upload' && (
                        <div className="mt-4 space-y-4">
                            <div>
                                <Label>Allowed File Types</Label>
                                <Input
                                    value={question.fileConfig?.allowedTypes?.join(', ') || ''}
                                    onChange={(e) => updateQuestion(
                                        category.id,
                                        question.id,
                                        'fileConfig',
                                        {
                                            ...question.fileConfig,
                                            allowedTypes: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                                        }
                                    )}
                                    placeholder="e.g., .pdf, .jpg, .png"
                                />
                                <p className="text-sm text-gray-500 mt-1">Enter file extensions separated by commas</p>
                            </div>

                            <div>
                                <Label>Max File Size (MB)</Label>
                                <Input
                                    type="number"
                                    value={question.fileConfig?.maxSize || 5}
                                    onChange={(e) => updateQuestion(
                                        category.id,
                                        question.id,
                                        'fileConfig',
                                        {
                                            ...question.fileConfig,
                                            maxSize: Number(e.target.value)
                                        }
                                    )}
                                    min="1"
                                    max="100"
                                />
                            </div>

                            <div className="flex items-center gap-2">
                                <Switch
                                    checked={question.fileConfig?.multiple || false}
                                    onCheckedChange={(checked) => updateQuestion(
                                        category.id,
                                        question.id,
                                        'fileConfig',
                                        {
                                            ...question.fileConfig,
                                            multiple: checked
                                        }
                                    )}
                                />
                                <Label>Allow Multiple Files</Label>
                            </div>
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label>Help Image</Label>
                        <div className="flex gap-4 items-start">
                            <div className="flex-1 space-y-4">
                                <div className="flex gap-2">
                                    <input
                                        type="file"
                                        id={`help-image-${question.id}`}
                                        className="hidden"
                                        accept="image/*"
                                        onChange={async (e) => {
                                            try {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    const result = await ProjectService.uploadAssetImage(file);
                                                    const updatedQuestion = {
                                                        ...question,
                                                        helpImage: result.url,
                                                        helpImageWidth: 300,
                                                        helpImageHeight: 200
                                                    };
                                                    setFormData({
                                                        ...formData,
                                                        [currentRoom]: {
                                                            ...formData[currentRoom],
                                                            categories: formData[currentRoom].categories.map(c => {
                                                                if (c.id === category.id) {
                                                                    return {
                                                                        ...c,
                                                                        questions: c.questions.map(q =>
                                                                            q.id === question.id ? updatedQuestion : q
                                                                        )
                                                                    };
                                                                }
                                                                return c;
                                                            })
                                                        }
                                                    });
                                                }
                                                // Reset the file input
                                                e.target.value = '';
                                            } catch (error) {
                                                console.error('Failed to upload help image:', error);
                                                // Reset the file input even if there's an error
                                                e.target.value = '';
                                            }
                                        }}
                                    />
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => document.getElementById(`help-image-${question.id}`).click()}
                                    >
                                        <Upload className="w-4 h-4 mr-1" />
                                        Upload Help Image
                                    </Button>
                                    {question.helpImage && (
                                        <Button
                                            size="sm"
                                            variant="destructive"
                                            onClick={() => {
                                                const { helpImage, helpImageWidth, helpImageHeight, ...restQuestion } = question;
                                                setFormData({
                                                    ...formData,
                                                    [currentRoom]: {
                                                        ...formData[currentRoom],
                                                        categories: formData[currentRoom].categories.map(c => {
                                                            if (c.id === category.id) {
                                                                return {
                                                                    ...c,
                                                                    questions: c.questions.map(q =>
                                                                        q.id === question.id ? restQuestion : q
                                                                    )
                                                                };
                                                            }
                                                            return c;
                                                        })
                                                    }
                                                });
                                                // Reset the file input after removal
                                                const fileInput = document.getElementById(`help-image-${question.id}`);
                                                if (fileInput) fileInput.value = '';
                                            }}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            Remove Image
                                        </Button>
                                    )}
                                </div>
                                {question.helpImage && (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label>Image Width (px)</Label>
                                            <Input
                                                type="number"
                                                value={question.helpImageWidth || 300}
                                                onChange={(e) => updateQuestion(category.id, question.id, 'helpImageWidth', Number(e.target.value))}
                                                min="100"
                                                max="800"
                                            />
                                        </div>
                                        <div>
                                            <Label>Image Height (px)</Label>
                                            <Input
                                                type="number"
                                                value={question.helpImageHeight || 200}
                                                onChange={(e) => updateQuestion(category.id, question.id, 'helpImageHeight', Number(e.target.value))}
                                                min="100"
                                                max="600"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                            {question.helpImage && (
                                <div className="w-48 h-32 border rounded overflow-hidden">
                                    <img 
                                        src={question.helpImage} 
                                        alt="Help visualization" 
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    const currentRoomData = formData?.[currentRoom] || { categories: [] };
const currentCategoryData = currentRoomData.categories?.find(c => c.id === currentCategory);

return (
 <div className="container mx-auto p-4 max-w-4xl">
   <div className="flex justify-between items-center mb-6">
     <div className="flex items-center gap-4">
       <Select 
            id={currentRoom} 
            onValueChange={(id) => {
                if (!formData[id]) {
                setFormData({
                    ...formData,
                    [id]: {
                    header: `${AVAILABLE_ROOM_TYPES.find(r => r.id === id)?.label} Design Questions`,
                    icon: '🏠',
                    categories: []
                    }
                });
                }
                setCurrentRoom(id);
            }}
            >
            <SelectTrigger className="w-[200px]">
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                <SelectGroup>
                <SelectLabel>Existing Rooms</SelectLabel>
                {Object.keys(formData || {}).map(room => (
                    <SelectItem key={room} value={room}>
                    {AVAILABLE_ROOM_TYPES.find(r => r.id === room)?.label || room}
                    </SelectItem>
                ))}
                </SelectGroup>
                <SelectGroup>
                <SelectLabel>Add New Room</SelectLabel>
                {AVAILABLE_ROOM_TYPES
                    .filter(room => !formData?.[room.id])
                    .map(room => (
                    <SelectItem key={room.id} value={room.id}>
                        {room.label}
                    </SelectItem>
                    ))}
                </SelectGroup>
            </SelectContent>
        </Select>
        {currentRoom && (
            <Button
            variant="destructive"
            size="sm"
            onClick={() => {
                const newFormData = { ...formData };
                delete newFormData[currentRoom];
                setFormData(newFormData);
                setCurrentRoom(Object.keys(newFormData)[0] || '');
                setCurrentCategory(null);
            }}
            >
            <Trash2 className="w-4 h-4" />
            </Button>
        )}
       <span className="text-4xl">{currentRoomData.icon}</span>
       <h1 className="text-2xl font-bold">{currentRoomData.header}</h1>
     </div>
     <div className="space-x-2">
        <Button 
            variant="outline" 
            onClick={handlePublish} 
            disabled={isPublishing}
        >
            <Upload className="w-4 h-4 mr-2" />
            {isPublishing ? 'Publishing...' : 'Publish'}
        </Button>
        <Button variant="outline" onClick={() => setShowPreview(true)}>
            <Eye className="w-4 h-4 mr-2" />
            Preview
        </Button>
       <Button variant="outline" onClick={() => setShowJson(!showJson)}>
         {showJson ? 'Hide' : 'Show'} JSON
       </Button>
     </div>
   </div>

   <Card className="mb-6">
     <CardHeader>
       <div className="grid grid-cols-2 gap-4">
         <div>
           <Label>Page Header</Label>
           <Input
             value={currentRoomData.header || ''}
             onChange={(e) => setFormData({
               ...formData,
               [currentRoom]: {
                 ...formData[currentRoom],
                 header: e.target.value
               }
             })}
             placeholder="Enter page header"
           />
         </div>
         <div>
           <Label>Icon</Label>
           <Input
             value={currentRoomData.icon || ''}
             onChange={(e) => setFormData({
               ...formData,
               [currentRoom]: {
                 ...formData[currentRoom],
                 icon: e.target.value
               }
             })}
             placeholder="Enter icon emoji"
           />
         </div>
       </div>
     </CardHeader>
   </Card>

   <div className="grid grid-cols-4 gap-4">
     <div className="space-y-2">
       {currentRoomData.categories.map(category => (
         <div
           key={category.id}
           className={`p-2 rounded cursor-pointer flex justify-between items-center ${
             currentCategory === category.id ? 'bg-blue-100' : 'bg-gray-100'
           }`}
           onClick={() => setCurrentCategory(category.id)}
         >
           <span>{category.icon} {category.name}</span>
           <Button
             size="sm"
             variant="destructive"
             onClick={(e) => {
               e.stopPropagation();
               removeCategory(category.id);
             }}
           >
             <Trash2 className="w-4 h-4" />
           </Button>
         </div>
       ))}
       <Button 
            onClick={addCategory}
            className="w-full mt-2"
            // variant="outline"
            >
            <PlusCircle className="w-4 h-4 mr-2" />
            Add Section
        </Button>
     </div>

     <div className="col-span-3">
       {currentCategoryData ? (
         <div className="space-y-4">
           <Card>
             <CardHeader>
               <div className="flex gap-2">
                 <div className="flex-1">
                   <Label>Section Name</Label>
                   <Input
                     value={currentCategoryData.name}
                     onChange={(e) => updateCategory(currentCategoryData.id, 'name', e.target.value)}
                     placeholder="Enter category name"
                   />
                 </div>
                 <div className="w-24">
                   <Label>Icon</Label>
                   <Input
                     value={currentCategoryData.icon}
                     onChange={(e) => updateCategory(currentCategoryData.id, 'icon', e.target.value)}
                     placeholder="Icon"
                   />
                 </div>
               </div>
             </CardHeader>
           </Card>

           <div className="flex justify-end">
             <Button onClick={() => addQuestion(currentCategoryData.id)}>
               <Plus className="w-4 h-4 mr-2" />
               Add Question
             </Button>
           </div>

           <div className="space-y-4">
             {currentCategoryData.questions.map(question => 
               renderQuestion(currentCategoryData, question)
             )}
           </div>
         </div>
       ) : (
         <div className="text-center text-gray-500 py-8">
           Select a category or create a new one to start building questions
         </div>
       )}
     </div>
   </div>

   {showJson && (
     <Card className="mt-6">
       <CardHeader>
         <h3 className="font-bold">Generated JSON</h3>
       </CardHeader>
       <CardContent>
         <pre className="bg-gray-100 p-4 rounded overflow-auto max-h-96">
           {JSON.stringify(formData, null, 2)}
         </pre>
       </CardContent>
     </Card>
   )}

    {showPreview && (
        <PreviewModal 
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        formData={formData}
        currentRoom={currentRoom}
    />
    )}
 </div>
);
};



export default QuestionBuilder;
